/**
 * Custom PricingLeverSliderComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OnDestroy, inject } from '@angular/core';
import { MatSliderModule } from '@angular/material/slider';
import { PricingLeverSliderComponent as ParentPricingLeverSliderComponent } from '@shared-lib/global-components/pricing-lever-slider/pricing-lever-slider.component';
import { Subscription } from 'rxjs';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';

@Component({
  selector: 'app-pricing-lever-slider',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSliderModule],
  templateUrl: './pricing-lever-slider.component.html',
  styleUrls: ['./pricing-lever-slider.component.scss']
})
export class PricingLeverSliderComponent extends ParentPricingLeverSliderComponent implements OnDestroy {
  // Constructor is inherited from parent to avoid dependency injection issues
  private readonly customConfigService = inject(ConfigService);
  private readonly customGlobalFilterService = inject(GlobalFilterService);
  private readonly customChartFilterHandler = inject(ChartFilterHandlerService);
  private debounceTimer?: ReturnType<typeof setTimeout>;
  private customChangeActionSub?: Subscription;
  private commitActionSub?: Subscription;
  private lastCommitSignature = '';
  private lastSubmittedValue: number | null = null;
  private pendingOriginalValue: number | null = null;
  hasPendingSubmit = false;
  isSubmitting = false;
  showSubmitConfirmation = false;

  override get formattedValue(): string {
    const value = this.displayValue ?? this.value;
    return value === null ? '' : this.formatCompactPercent(value);
  }

  get submitButtonLabel(): string {
    return String((this.data as any)?.submitButtonLabel || 'Submit').trim() || 'Submit';
  }

  get confirmYesLabel(): string {
    return String((this.data as any)?.submitConfirmYesLabel || 'Yes').trim() || 'Yes';
  }

  get confirmNoLabel(): string {
    return String((this.data as any)?.submitConfirmNoLabel || 'No').trim() || 'No';
  }

  get submitConfirmationNote(): string {
    return String(
      (this.data as any)?.submitConfirmationNote ||
      'Note: The engine model price change % will be updated based on the slider value.',
    ).trim();
  }

  get submitConfirmationNoteLabel(): string {
    return 'Note:';
  }

  get submitConfirmationNoteBody(): string {
    return this.submitConfirmationNote.replace(/^Note\s*:\s*/i, '').trim();
  }

  override onSliderInput(nextValue: any): any {
    if (!this.userInputEnabled) {
      return;
    }

    this.clearDebounceTimer();
    const previousValue = this.value;
    this.setPreviewValue(nextValue);

    if (this.usesManualSubmit()) {
      this.capturePendingOriginalValue(previousValue, this.value);
      this.hasPendingSubmit = this.hasSliderValueChanged();
      if (!this.hasPendingSubmit) {
        this.pendingOriginalValue = null;
      }
    }
  }

  onSliderCommit(eventOrValue?: any): void {
    if (!this.userInputEnabled) {
      return;
    }

    const nextValue = eventOrValue?.value ?? eventOrValue ?? this.value;
    this.clearDebounceTimer();
    const previousValue = this.value;
    this.setPreviewValue(nextValue);

    if (this.usesManualSubmit()) {
      this.capturePendingOriginalValue(previousValue, this.value);
      this.hasPendingSubmit = this.hasSliderValueChanged();
      if (!this.hasPendingSubmit) {
        this.pendingOriginalValue = null;
      }
      return;
    }

    this.scheduleDelayedCommit(this.value);
  }

  override onInputChange(): any {
    if (!this.userInputEnabled) {
      return;
    }

    this.clearDebounceTimer();
    const previousValue = this.value;
    this.setPreviewValue(this.inputValue);

    if (this.usesManualSubmit()) {
      this.capturePendingOriginalValue(previousValue, this.value);
      this.hasPendingSubmit = this.hasSliderValueChanged();
      if (!this.hasPendingSubmit) {
        this.pendingOriginalValue = null;
      }
      return;
    }

    this.scheduleDelayedCommit(this.value);
  }

  onSubmitClick(): void {
    if (!this.userInputEnabled) {
      return;
    }

    if (!this.usesManualSubmit() || this.value === null || this.value === undefined || !this.hasSliderValueChanged()) {
      return;
    }

    this.clearDebounceTimer();
    if (this.usesSubmitConfirmation()) {
      this.showSubmitConfirmation = true;
      return;
    }
    this.executeCommit(this.value);
  }

  onConfirmSubmit(): void {
    if (!this.userInputEnabled) {
      return;
    }

    if (this.value === null || this.value === undefined) {
      this.showSubmitConfirmation = false;
      return;
    }

    this.showSubmitConfirmation = false;
    this.executeCommit(this.value);
  }

  onCancelSubmit(): void {
    const revertValue = this.pendingOriginalValue ?? this.resolveCurrentCommittedValue();
    this.showSubmitConfirmation = false;
    this.hasPendingSubmit = false;
    this.pendingOriginalValue = null;

    if (revertValue === null || revertValue === undefined) {
      return;
    }

    this.setPreviewValue(revertValue);
  }

  override ngOnDestroy(): void {
    this.clearDebounceTimer();
    this.customChangeActionSub?.unsubscribe();
    this.commitActionSub?.unsubscribe();
    super.ngOnDestroy();
  }

  private clearDebounceTimer(): void {
    if (!this.debounceTimer) {
      return;
    }

    clearTimeout(this.debounceTimer);
    this.debounceTimer = undefined;
  }

  private setPreviewValue(rawValue: any): void {
    if (this.min === null || this.max === null || this.step === null) {
      return;
    }

    const parsed = Number(rawValue);
    const fallback = this.value ?? this.min;
    const nextValue = Number.isFinite(parsed) ? parsed : fallback;
    const clampedValue = Math.min(this.max, Math.max(this.min, nextValue));
    const decimals = this.countDecimalPlaces(this.step);
    this.value = Number(clampedValue.toFixed(decimals));
    this.displayValue = this.value;
    this.inputValue = this.value;
  }

  private resolveDebounceMs(): number {
    const config = this.data as any;
    const configured = Number(
      config?.sliderCommitDebounceMs ??
      config?.commitDebounceMs ??
      config?.debounceMs,
    );

    return Number.isFinite(configured) && configured >= 0 ? configured : 400;
  }

  private scheduleDelayedCommit(value: number | null): void {
    if (value === null || value === undefined) {
      return;
    }

    const debounceMs = this.resolveDebounceMs();
    if (debounceMs <= 0) {
      this.executeCommit(value);
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = undefined;
      this.executeCommit(value);
    }, debounceMs);
  }

  private executeCommit(value: number): void {
    const filterKey = this.data?.filterKey || this.data?.key;
    const filterPayload = filterKey ? this.buildCustomFilterPayload(filterKey, value) : {};
    const nextSignature = this.buildCommitSignature(filterPayload);

    if (!this.shouldCommitFilterPayload(filterPayload, nextSignature)) {
      this.hasPendingSubmit = false;
      this.lastSubmittedValue = value;
      this.pendingOriginalValue = null;
      this.showSubmitConfirmation = false;
      return;
    }

    this.lastCommitSignature = nextSignature;
    this.lastSubmittedValue = value;
    this.hasPendingSubmit = false;
    this.pendingOriginalValue = null;
    this.showSubmitConfirmation = false;
    this.isSubmitting = true;
    this.valueChange.emit(value);

    if (filterKey) {
      this.runChangeAction(filterPayload);
      this.customChartFilterHandler.updateFilterSelection(
        { ...this.data, filterKey },
        filterPayload,
        filterPayload,
      );
    }

    this.runCommitAction(value);
  }

  private buildCustomFilterPayload(filterKey: string | string[], value: number): Record<string, number> {
    const keys = Array.isArray(filterKey)
      ? filterKey
      : String(filterKey)
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean);

    return keys.reduce<Record<string, number>>((payload, key) => {
      payload[key] = value;
      return payload;
    }, {});
  }

  private shouldCommitFilterPayload(filterPayload: Record<string, number>, nextSignature: string): boolean {
    if (!Object.keys(filterPayload).length) {
      return true;
    }

    if (this.lastCommitSignature && this.lastCommitSignature === nextSignature) {
      return false;
    }

    const currentFilters = this.customGlobalFilterService.getCurrentFilters() || {};
    return Object.entries(filterPayload).some(([key, value]) => {
      const currentValue =
        currentFilters?.requestParams?.[key] ??
        currentFilters?.queryParams?.[key];
      return Number(currentValue) !== Number(value);
    });
  }

  private buildCommitSignature(filterPayload: Record<string, number>): string {
    return JSON.stringify(
      Object.keys(filterPayload)
        .sort()
        .reduce<Record<string, number>>((payload, key) => {
          payload[key] = filterPayload[key];
          return payload;
        }, {}),
    );
  }

  private runChangeAction(changedRequestData: Record<string, number>): void {
    const action = (this.data as any)?.changeAction;
    if (!action?.apiUrl) {
      return;
    }

    const actionRequestData = this.buildCustomChangeActionRequestData(action, changedRequestData);
    const requestConfig = this.withCustomChangedRequestKeys(action, actionRequestData, changedRequestData);
    this.customChangeActionSub?.unsubscribe();
    this.customChangeActionSub = this.customConfigService
      .getConfig(action.apiUrl, requestConfig, { requestParams: actionRequestData }, action.method)
      .subscribe({
        error: (error) => console.error('[PricingLeverSliderComponent] change action failed:', error),
      });
  }

  private withCustomChangedRequestKeys(
    action: any,
    actionRequestData: Record<string, any>,
    changedRequestData: Record<string, number>,
  ): any {
    const configuredKeys = Array.isArray(action.requestFilterKeys) ? action.requestFilterKeys : [];

    return {
      ...action,
      includeGlobalExcludeFilterKeys: true,
      requestFilterKeys: Array.from(new Set([
        ...configuredKeys,
        ...Object.keys(changedRequestData),
        ...Object.keys(actionRequestData),
      ])),
    };
  }

  private buildCustomChangeActionRequestData(
    action: any,
    changedRequestData: Record<string, number>,
  ): Record<string, any> {
    const actionKeys = action.requestFilterKeys;
    if (!Array.isArray(actionKeys) || actionKeys.length === 0) {
      return changedRequestData;
    }

    const currentFilters = this.customGlobalFilterService.getCurrentFilters() || {};
    const requestData = actionKeys.reduce<Record<string, any>>((payload, key) => {
      const value =
        currentFilters?.requestParams?.[key] ??
        currentFilters?.queryParams?.[key];

      if (value !== undefined && value !== null && value !== '') {
        payload[key] = this.normalizeCustomRequestValue(value);
      }

      return payload;
    }, {});

    return {
      ...(action.requestPayload || {}),
      ...requestData,
      ...changedRequestData,
    };
  }

  private normalizeCustomRequestValue(value: any): any {
    if (Array.isArray(value)) {
      return value.length === 1 ? this.normalizeCustomRequestValue(value[0]) : value.map((item) => this.normalizeCustomRequestValue(item));
    }

    return value;
  }

  private runCommitAction(value: number | null): void {
    if (value === null || value === undefined) {
      this.isSubmitting = false;
      return;
    }

    const config = this.data as any;
    const action = config?.commitAction ?? config?.submitAction;
    if (!action?.apiUrl) {
      this.isSubmitting = false;
      return;
    }

    this.commitActionSub?.unsubscribe();

    const requestData = this.resolveDynamicValue(action.requestData ?? action.customPayload ?? {}, value);
    const requestConfig = {
      ...action,
      includeGlobalExcludeFilterKeys: true,
      widgetId: `${this.data?.widgetId || 'pricing-lever-slider'}-commit-action`,
      customPayload: requestData,
      functionName: action.functionName || config?.functionName,
      entity: action.entity || config?.entity,
      workflowId: action.workflowId || config?.workflowId,
      version: action.version || config?.version,
      headers: action.headers || config?.headers,
    };
    const requestFilters = {
      requestParams: requestData,
      queryParams: {},
      useQueryParams: action.useQueryParams === true,
      headers: action.headers || config?.headers,
    };

    this.commitActionSub = this.customConfigService
      .getConfig(action.apiUrl, requestConfig, requestFilters, action.method || 'post')
      .subscribe({
        next: () => {
          this.isSubmitting = false;
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('[PricingLeverSliderComponent] commit action failed:', error);
        },
        complete: () => {
          this.isSubmitting = false;
        },
      });
  }

  private usesManualSubmit(): boolean {
    return (this.data as any)?.showSubmitOnChange === true;
  }

  private usesSubmitConfirmation(): boolean {
    return (this.data as any)?.showSubmitConfirmation === true;
  }

  private hasSliderValueChanged(): boolean {
    if (this.value === null || this.value === undefined) {
      return false;
    }

    if (this.lastSubmittedValue !== null && this.lastSubmittedValue !== undefined) {
      return Math.abs(this.value - this.lastSubmittedValue) > 1e-9;
    }

    const filterKey = this.data?.filterKey || this.data?.key;
    const filterKeys = Array.isArray(filterKey)
      ? filterKey
      : String(filterKey || '')
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean);
    const currentFilters = this.customGlobalFilterService.getCurrentFilters() || {};

    if (filterKeys.length === 0) {
      return true;
    }

    return filterKeys.some((key) => {
      const currentValue =
        currentFilters?.requestParams?.[key] ??
        currentFilters?.queryParams?.[key];
      return Number(currentValue) !== Number(this.value);
    });
  }

  private capturePendingOriginalValue(previousValue: number | null, nextValue: number | null): void {
    if (this.pendingOriginalValue !== null && this.pendingOriginalValue !== undefined) {
      return;
    }

    if (previousValue === null || previousValue === undefined || nextValue === null || nextValue === undefined) {
      return;
    }

    if (Math.abs(previousValue - nextValue) <= 1e-9) {
      return;
    }

    this.pendingOriginalValue = previousValue;
  }

  private resolveCurrentCommittedValue(): number | null {
    if (this.lastSubmittedValue !== null && this.lastSubmittedValue !== undefined) {
      return this.lastSubmittedValue;
    }

    const filterKey = this.data?.filterKey || this.data?.key;
    const firstKey = Array.isArray(filterKey)
      ? filterKey[0]
      : String(filterKey || '')
        .split(',')
        .map((key) => key.trim())
        .find(Boolean);
    const currentFilters = this.customGlobalFilterService.getCurrentFilters() || {};
    const currentValue = firstKey
      ? currentFilters?.requestParams?.[firstKey] ?? currentFilters?.queryParams?.[firstKey]
      : null;
    const parsedCurrentValue = Number(currentValue);

    if (Number.isFinite(parsedCurrentValue)) {
      return parsedCurrentValue;
    }

    return this.value;
  }

  private resolveDynamicValue(value: any, sliderValue: number): any {
    if (typeof value === 'string') {
      return value === '$value' ? sliderValue : value.replace(/\$value/g, String(sliderValue));
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.resolveDynamicValue(item, sliderValue));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).reduce<Record<string, any>>((acc, key) => {
        acc[key] = this.resolveDynamicValue(value[key], sliderValue);
        return acc;
      }, {});
    }

    return value;
  }

  private formatCompactPercent(value: number): string {
    const configuredDecimals = Number(
      (this.data as any)?.displayDecimals ??
      (this.data as any)?.valueDecimals ??
      (this.data as any)?.decimals,
    );
    const decimals = Number.isInteger(configuredDecimals) && configuredDecimals >= 0
      ? Math.min(configuredDecimals, 10)
      : this.step === null ? 2 : Math.min(2, Math.max(0, this.countDecimalPlaces(this.step)));
    return `${Number(value.toFixed(decimals)).toString()}${this.suffix}`;
  }

  private countDecimalPlaces(value: number): number {
    const text = String(value);
    return text.includes('.') ? text.split('.')[1].length : 0;
  }
}
